import { useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import {
  Loader2,
  BadgeAlert,
  ChevronLeft,
  ChevronRight,
  CalendarClock,
  Dumbbell,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  useUpcomingExpirations,
  type ExpirationDay,
  type ExpirationItem,
} from '@/api/analytics';

// Monday-first week, matching EU convention.
const WEEK_STARTS_ON = 1 as const;
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function ExpirationItemRow({ item }: { item: ExpirationItem }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b last:border-b-0">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">
          {item.surname} {item.name}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {item.email || '—'}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-xs text-muted-foreground truncate max-w-[140px]">
          {item.planName ?? 'Unknown plan'}
        </span>
        {item.isClass ? (
          <Badge variant="outline" className="text-xs">
            class
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs">
            gym
          </Badge>
        )}
      </div>
    </div>
  );
}

export function ExpirationCalendar() {
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(new Date()),
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const monthStart = useMemo(() => startOfMonth(viewMonth), [viewMonth]);
  const monthEnd = useMemo(() => endOfMonth(viewMonth), [viewMonth]);

  const from = toKey(monthStart);
  const to = toKey(monthEnd);

  const { data, isLoading, isError, error } = useUpcomingExpirations(from, to);

  // Index expiration days by their YYYY-MM-DD key for O(1) cell lookup.
  const daysByDate = useMemo(() => {
    const map = new Map<string, ExpirationDay>();
    for (const d of data?.days ?? []) map.set(d.date, d);
    return map;
  }, [data]);

  // Full calendar grid: pad to whole weeks around the month.
  const gridDays = useMemo(() => {
    const gridStart = startOfWeek(monthStart, { weekStartsOn: WEEK_STARTS_ON });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: WEEK_STARTS_ON });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [monthStart, monthEnd]);

  const today = useMemo(() => new Date(), []);
  const selectedDay = selectedDate ? daysByDate.get(selectedDate) : undefined;

  const goPrev = () => {
    setViewMonth((m) => subMonths(m, 1));
    setSelectedDate(null);
  };
  const goNext = () => {
    setViewMonth((m) => addMonths(m, 1));
    setSelectedDate(null);
  };
  const goToday = () => {
    setViewMonth(startOfMonth(new Date()));
    setSelectedDate(null);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={goPrev}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={goNext}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
        </div>
        <h3 className="text-lg font-semibold">{format(viewMonth, 'MMMM yyyy')}</h3>

        <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
          {data && (
            <>
              <Badge variant="secondary" className="flex items-center gap-1">
                <CalendarClock className="h-3 w-3" />
                {data.total} expiring
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Dumbbell className="h-3 w-3" />
                {data.gymTotal} gym
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {data.classTotal} class
              </Badge>
            </>
          )}
        </div>
      </div>

      {isError && (
        <div className="border rounded-md p-4 text-sm text-destructive flex items-center gap-2">
          <BadgeAlert className="h-4 w-4" />
          {(error as Error)?.message || 'Failed to load expirations.'}
        </div>
      )}

      {/* Calendar grid */}
      <div className="border rounded-md overflow-hidden relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Loading…
          </div>
        )}

        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b bg-muted/40">
          {WEEKDAY_LABELS.map((w) => (
            <div
              key={w}
              className="px-2 py-1.5 text-xs font-medium text-muted-foreground text-center select-none"
            >
              {w}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {gridDays.map((day) => {
            const key = toKey(day);
            const bucket = daysByDate.get(key);
            const inMonth = isSameMonth(day, viewMonth);
            const isToday = isSameDay(day, today);
            const isSelected = selectedDate === key;
            const count = bucket?.count ?? 0;

            return (
              <button
                key={key}
                type="button"
                disabled={!bucket}
                onClick={() => bucket && setSelectedDate(key)}
                className={cn(
                  'min-h-[72px] border-b border-r p-1.5 flex flex-col items-start gap-1 text-left transition-colors',
                  '[&:nth-child(7n)]:border-r-0',
                  inMonth ? 'bg-background' : 'bg-muted/20',
                  bucket && 'hover:bg-accent cursor-pointer',
                  !bucket && 'cursor-default',
                  isSelected && 'ring-2 ring-inset ring-primary',
                )}
              >
                <span
                  className={cn(
                    'inline-flex h-6 w-6 items-center justify-center rounded-full text-xs',
                    inMonth
                      ? 'text-foreground'
                      : 'text-muted-foreground/60',
                    isToday && 'bg-primary text-primary-foreground font-semibold',
                  )}
                >
                  {format(day, 'd')}
                </span>

                {count > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <Badge
                      variant="secondary"
                      className="px-1.5 py-0 text-[10px] leading-5"
                      title={`${count} payment${count === 1 ? '' : 's'} expire`}
                    >
                      {count}
                    </Badge>
                    {bucket!.classCount > 0 && (
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-purple-500 mt-1.5"
                        title={`${bucket!.classCount} class`}
                      />
                    )}
                    {bucket!.gymCount > 0 && (
                      <span
                        className="inline-block w-2 h-2 rounded-full bg-blue-500 mt-1.5"
                        title={`${bucket!.gymCount} gym`}
                      />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
          Gym access
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-purple-500" />
          Class access
        </div>
        <span>Only date-based plans appear — fixed (entry-based) plans expire by usage.</span>
      </div>

      {/* Selected-day detail */}
      {selectedDay && (
        <div className="border rounded-md p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">
              {format(parseISO(selectedDay.date), 'EEEE, d MMMM yyyy')}
            </h4>
            <Badge variant="secondary">
              {selectedDay.count} expiring
            </Badge>
          </div>
          <div className="divide-y">
            {selectedDay.items.map((item, i) => (
              <ExpirationItemRow key={`${item.paymentId}-${i}`} item={item} />
            ))}
          </div>
        </div>
      )}

      {!isLoading && data && data.total === 0 && (
        <div className="text-sm text-muted-foreground">
          No plans expire in {format(viewMonth, 'MMMM yyyy')}.
        </div>
      )}
    </div>
  );
}
